import React, { useEffect, useState } from 'react';
import { Search, Building, X, Loader2, Edit, Trash, AlertTriangle, Calendar, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Organization, OrganizationUpdatePayload } from '../types';
import api from '../services/api';
import { ToastContainer } from '../components/ui/Toast';

// Simple Modal Component
const Modal = ({ isOpen, onClose, title, children }: { isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-slate-800">
                    <h3 className="text-xl font-semibold text-white">{title}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6">
                    {children}
                </div>
            </div>
        </div>
    );
};

const Clients: React.FC = () => {
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modals state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Form state
    const [editFormData, setEditFormData] = useState<OrganizationUpdatePayload>({});

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const data = await api.getOrganizations();
            setOrganizations(data);
        } catch (error) {
            console.error('Error fetching organizations:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleEditClick = (org: Organization) => {
        setSelectedOrg(org);
        setEditFormData({
            name: org.name,
            trial_end: org.trial_end ? org.trial_end.split('T')[0] : '',
            is_active: org.is_active
        });
        setIsEditModalOpen(true);
    };

    const handleDeleteClick = (org: Organization) => {
        setSelectedOrg(org);
        setIsDeleteModalOpen(true);
    };

    const handleUpdateOrg = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedOrg) return;

        setIsSaving(true);
        try {
            // Format date back to ISO if present
            const payload = { ...editFormData };
            if (payload.trial_end) {
                payload.trial_end = new Date(payload.trial_end).toISOString();
            }

            await api.updateOrganization(selectedOrg.id, payload);
            setIsEditModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error updating organization:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedOrg) return;
        setIsSaving(true);
        try {
            await api.deleteOrganization(selectedOrg.id);
            setIsDeleteModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error deleting organization:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const filteredOrgs = organizations.filter(org =>
        org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.id.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-6 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white tracking-tight">Gestión de Clientes</h1>
                    <p className="text-slate-400 mt-1">Administración de organizaciones y periodos de prueba (Multi-Tenancy).</p>
                </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                            <Building className="w-6 h-6 text-blue-500" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">Total Clientes</p>
                            <p className="text-2xl font-bold text-white mt-0.5">{organizations.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                            <ShieldCheck className="w-6 h-6 text-emerald-500" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">Activos</p>
                            <p className="text-2xl font-bold text-white mt-0.5">{organizations.filter(o => o.is_active).length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                            <Calendar className="w-6 h-6 text-amber-500" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">En Trial</p>
                            <p className="text-2xl font-bold text-white mt-0.5">
                                {organizations.filter(o => o.trial_end && new Date(o.trial_end) > new Date()).length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6 border-b border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900/50">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                            type="text"
                            placeholder="Buscar por nombre o ID..."
                            className="w-full bg-slate-950 border border-slate-700 rounded-xl py-2 pl-10 pr-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-950/50 border-b border-slate-800">
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Organización</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Slug / ID</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Estado</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Trial End</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-20 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                                            <p className="text-slate-500 font-medium">Cargando organizaciones...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredOrgs.length > 0 ? (
                                filteredOrgs.map((org) => (
                                    <tr key={org.id} className="hover:bg-slate-800/30 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-blue-400 transition-colors">
                                                    <Building className="w-5 h-5" />
                                                </div>
                                                <span className="font-semibold text-white">{org.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <code className="text-xs bg-slate-950 px-2 py-1 rounded border border-slate-800 text-slate-400">
                                                {org.id}
                                            </code>
                                        </td>
                                        <td className="px-6 py-4">
                                            {org.is_active ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                                                    <ShieldCheck className="w-3 h-3" /> Activo
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-500/10 text-slate-400 border border-slate-500/20">
                                                    <ShieldAlert className="w-3 h-3" /> Inactivo
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="text-sm text-slate-300">
                                                    {org.trial_end ? new Date(org.trial_end).toLocaleDateString() : 'Sin Trial'}
                                                </span>
                                                {org.trial_end && new Date(org.trial_end) < new Date() && (
                                                    <span className="text-[10px] font-bold text-red-500 uppercase mt-0.5">Expirado</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => handleEditClick(org)}
                                                    className="p-2 hover:bg-blue-500/10 text-slate-400 hover:text-blue-400 rounded-lg transition-colors"
                                                    title="Editar Organización"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteClick(org)}
                                                    className="p-2 hover:bg-red-500/10 text-slate-400 hover:text-red-400 rounded-lg transition-colors"
                                                    disabled={org.id === 'default-org'}
                                                    title="Eliminar"
                                                >
                                                    <Trash className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="px-6 py-20 text-center text-slate-500">
                                        No se encontraron organizaciones.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit Modal */}
            <Modal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                title="Editar Organización"
            >
                <form onSubmit={handleUpdateOrg} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-400 mb-1.5">Nombre de la Organización</label>
                        <input
                            type="text"
                            required
                            className="w-full bg-slate-950 border border-slate-700 rounded-xl py-2.5 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                            value={editFormData.name}
                            onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-400 mb-1.5">Fecha Fin de Trial</label>
                        <input
                            type="date"
                            className="w-full bg-slate-950 border border-slate-700 rounded-xl py-2.5 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                            value={editFormData.trial_end}
                            onChange={(e) => setEditFormData({ ...editFormData, trial_end: e.target.value })}
                        />
                    </div>
                    <div className="flex items-center gap-3 pt-2">
                        <input
                            type="checkbox"
                            id="is_active"
                            className="w-5 h-5 rounded border-slate-700 bg-slate-950 text-blue-500 focus:ring-blue-500/50 transition-all"
                            checked={editFormData.is_active}
                            onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                        />
                        <label htmlFor="is_active" className="text-sm font-medium text-white select-none cursor-pointer">
                            Estado Activo (Habilita el acceso total)
                        </label>
                    </div>

                    <div className="flex justify-end gap-3 mt-8">
                        <button
                            type="button"
                            onClick={() => setIsEditModalOpen(false)}
                            className="px-6 py-2.5 text-slate-400 hover:text-white transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all font-bold flex items-center gap-2 shadow-lg shadow-blue-500/20 disabled:opacity-50"
                        >
                            {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                            Guardar Cambios
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                title="Eliminar Organización"
            >
                <div className="space-y-6">
                    <div className="flex items-center gap-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                        <AlertTriangle className="w-8 h-8 text-red-500 flex-shrink-0" />
                        <div>
                            <p className="text-white font-bold">¿Estás absolutamente seguro?</p>
                            <p className="text-sm text-red-100/70 mt-0.5">
                                Esta acción es irreversible. Se eliminarán (soft-delete) todos los datos asociados a <strong>{selectedOrg?.name}</strong>.
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => setIsDeleteModalOpen(false)}
                            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
                            disabled={isSaving}
                        >
                            No, mantener
                        </button>
                        <button
                            onClick={handleDeleteConfirm}
                            disabled={isSaving}
                            className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all font-bold flex items-center gap-2"
                        >
                            {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                            Sí, eliminar definitivamente
                        </button>
                    </div>
                </div>
            </Modal>

            <ToastContainer toasts={[]} removeToast={() => { }} />
        </div>
    );
};

export default Clients;
